<template>
  <div
    v-show="!preview"
    ref="wrapper"
    class="lp-rule-editor w-100"
    :class="[
      isDarkThemeEnabled && 'lp-rule-editor-dark',
      isReadWriteModeEnabled && 'lp-rule-editor-read-write-mode',
    ]"
    @click="enableHasEdits()"
    @focusout="disableHasEdits()"
  >
    <div
      v-if="isReadWriteModeEnabled"
      class="lp-rule-editor-top-bar ly-end-center ly-row xp-2"
      :class="
        isDarkThemeEnabled
          ? 'bg-black-70'
          : 'bg-black-10 border border-black-20'
      "
    >
      <lp-switch
        active-icon="dark-mode"
        inactive-icon="light-mode"
        v-model="isDarkThemeEnabled"
        @update:model-value="handleDarkThemeUpdate"
      />
    </div>
    <span :id="id" />
    <div
      v-if="isReadWriteModeEnabled"
      class="lp-rule-editor-bar ly-spacebetween-start ly-row xp-2"
      :class="
        isDarkThemeEnabled
          ? 'bg-black-70 text-white'
          : 'bg-black-10 border border-black-20'
      "
    >
      <span class="xpt-1">
        <lp-icon
          :name="!hasError ? 'success' : 'error'"
          :color="!hasError ? 'success' : 'error'"
        />
        {{ ruleOutput }}
      </span>
      <div class="ly-row">
        <lp-tooltip :content="t('LpRuleEditor.Copy')">
          <lp-button text size="xs" icon="copy" @click="copyCode" />
        </lp-tooltip>
        <lp-tooltip :content="t('LpRuleEditor.Cut')">
          <lp-button
            text
            size="xs"
            icon="content-cut"
            divided
            @click="cutCode"
          />
        </lp-tooltip>
        <lp-tooltip :content="t('LpRuleEditor.FormatCode')">
          <lp-button text size="xs" icon="format-text" @click="formatCode" />
        </lp-tooltip>
      </div>
    </div>
  </div>
  <span v-show="preview" class="lp-rule-editor-preview">
    {{ modelValue }}
  </span>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, watchEffect } from 'vue'
import { render } from 'rule-editor-component'
import { t } from '@/plugins/i18n'
import './theme/base.scss'
import './theme/dark.scss'
import { Message } from '@/index'

defineOptions({
  name: 'lp-rule-editor',
})

enum ModeTypes {
  ReadWrite = 'read-write',
  ReadOnly = 'read-only',
}

type ContextVariableType = {
  name: string
  description: string
}

interface Props {
  modelValue: string | Promise<string>
  mode?: ModeTypes
  contextVariables?: ContextVariableType[] | null
  darkTheme?: boolean
  preview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  mode: ModeTypes.ReadWrite,
  contextVariables: null,
  darkTheme: false,
  preview: false,
})
const emit = defineEmits(['update:modelValue', 'darkThemeSelected'])
const onChangeHandler = (e) => {
  hasError.value = Boolean(e.error)
  ruleOutput.value = hasError.value ? e.error : e.output
  emit('update:modelValue', e.state.doc.text.join('\n'))
}
const isReadWriteModeEnabled = computed(
  () => props.mode === ModeTypes.ReadWrite,
)

const isDarkThemeEnabled = ref(props.darkTheme)

const handleDarkThemeUpdate = () => {
  emit('darkThemeSelected', isDarkThemeEnabled)
}

const copyCode = () => {
  if (!props.modelValue) return
  navigator.clipboard.writeText(String(props.modelValue))
  Message.success({
    text: t('LpRuleEditor.CodeCopied'),
    duration: 2000,
    show: true,
  })
}
const cutCode = () => {
  if (!props.modelValue) return
  navigator.clipboard.writeText(String(props.modelValue))
  emit('update:modelValue', '')
  Message.success({
    text: t('LpRuleEditor.CodeCut'),
    duration: 2000,
    show: true,
  })
}

const hasError = ref(false)
const ruleOutput = ref('')
const randomNumber = () => Math.floor(Math.random() * 10000000 + 1)

const id = ref(`rule-editor-${randomNumber()}-${randomNumber()}`)
const wrapper = ref<HTMLElement | null>(null)
const hasEdits = ref(false)
const enableHasEdits = () => (hasEdits.value = true)
const disableHasEdits = () => (hasEdits.value = false)

const renderEditor = () => {
  render(id.value, {
    code: props.modelValue,
    mode: props.mode,
    onChange: onChangeHandler,
    contextVariables: props.contextVariables,
  })
}

const formatCode = async () => {
  const editorElement = wrapper.value?.querySelector('.cm-content')
  editorElement?.focus()
  await nextTick()
  editorElement?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Tab' }))
  Message.success({
    text: t('LpRuleEditor.CodeFormatted'),
    duration: 2000,
    show: true,
  })
}
onMounted(() => {
  renderEditor()
})

watchEffect(() => {
  if (wrapper.value && !hasEdits.value) {
    renderEditor()
  }
})

watch(
  () => props.darkTheme,
  (newValue) => {
    isDarkThemeEnabled.value = newValue
  },
)
</script>
